import { inject, Injectable, Injector } from '@angular/core';

import { Store } from '@ngrx/store';

import * as ConfigActions from '@app-store/config/config.actions';
import * as fromRoot from '@app-store/reducers';
import { OktaSignIn } from '@okta/okta-signin-widget';

import { OktaService } from './okta.service';

@Injectable({
  providedIn: 'root',
})
export class LazyOktaService {
  private _oktaService: OktaService | undefined;
  private injector: Injector = inject(Injector);
  private store$: Store<fromRoot.State> = inject(Store<fromRoot.State>);

  async getOktaAuth() {
    const service = await this._initOktaService();

    return service.getOktaAuth();
  }

  async getOktaWidget(): Promise<OktaSignIn> {
    const service = await this._initOktaService();

    return service.getOktaWidget();
  }

  async oktaSignOut(): Promise<void> {
    const service = await this._initOktaService();

    return service.oktaSignOut();
  }

  async hasIdpSession(): Promise<boolean> {
    const service = await this._initOktaService();

    return service.hasIdpSession();
  }

  async captureBoundIdpSession(): Promise<void> {
    const service = await this._initOktaService();

    return service.captureBoundIdpSession();
  }

  async isBoundIdpSessionCurrent(): Promise<boolean> {
    const service = await this._initOktaService();

    return service.isBoundIdpSessionCurrent();
  }

  async isIncognitoBrowser(): Promise<boolean> {
    const service = await this._initOktaService();

    return service.isIncognitoBrowser();
  }

  async closeIdpSessionWithoutHint(): Promise<void> {
    const service = await this._initOktaService();

    return service.closeIdpSessionWithoutHint();
  }

  private async get<T>(providerLoader: () => Promise<any>) {
    return this.injector.get(await providerLoader());
  }

  private async _initOktaService(): Promise<OktaService> {
    if (!this._oktaService) {
      this.store$.dispatch(new ConfigActions.SetOktaLoadingAction());
      this._oktaService = (await this.get<OktaService>(() =>
        import('./okta.service').then((m) => m.OktaService)
      )) as OktaService;
      this.store$.dispatch(new ConfigActions.ResetOktaLoadingAction());
    }

    return Promise.resolve(this._oktaService);
  }
}
